/**
 * Discount API contract — response zod schema (single source of truth).
 *
 * GET /v1/prices/discounted response shape. Consolidated here from the api route
 * (api/src/routes/discount.ts) and the client data-fetcher (client/lib/data/discount.ts)
 * local copies, which were duplicated verbatim.
 *
 * Source: 2026-06-04-wandero-discount-engine §6;
 *         dedup 2026-06-25-wandero-discount-api-helpers-dedup.
 *
 * API-changelog (2026-07-04, 2026-07-04-wandero-discount-stacking-engine spec §6):
 * `applied` changed from a nullable single-candidate object to an array of
 * candidates (summation stacking — multiple discounts can now apply at once).
 * This is a type-change on an existing field, formally breaking under the
 * `/v1` freeze policy (docs/SPEC-API.md §9.2). Waiver granted in-place (no /v2
 * bump) because `/v1/prices/discounted` is NOT in the frozen public-contract
 * list (§9.1) — it was added post-freeze (2026-06-04) — and its sole consumer
 * is the storefront-BFF (`client/lib/data/discount.ts`), updated atomically in
 * the same PR. No external/third-party consumers exist.
 */

import { z } from 'zod';

/** A single applied discount candidate (schedule or segment rule), as serialised in the response. */
export const DiscountCandidateSchema = z.object({
  source: z.enum(['schedule', 'segment']),
  ref_id: z.number().int(),
  discount_type: z.enum(['pct', 'amount']),
  discount_value: z.string(),
});

export const DiscountedPriceResponseSchema = z.object({
  currency: z.string(),
  /** null when no active price exists for this material+currency. */
  base_unit_price: z.string().nullable(),
  /** null when base is null or no discount applies (final === base). */
  final_unit_price: z.string().nullable(),
  /** All positive-saving applied discount candidates (summation stacking); empty array = none applied. */
  applied: z.array(DiscountCandidateSchema),
});
export type DiscountedPriceResponse = z.infer<typeof DiscountedPriceResponseSchema>;
